import type { ShopperExperience } from '@/scapi';
import type { ComponentType } from '@/components/region';

export const GLOBAL_CATEGORY_SUBMENU_SLOTS_REGION_ID = 'categorySubmenuSlots';

export const CATEGORY_SUBMENU_SLOT_CONTENT_REGION_ID = 'content';

export type CategorySubmenuSlots = Record<string, ComponentType>;

function getDataRecord(data: unknown): Record<string, unknown> {
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

function endsWithTypeId(typeId: string, shortId: string): boolean {
    return typeId === shortId || typeId.endsWith(`.${shortId}`);
}

function isCategorySubmenuSlotComponent(typeId: string): boolean {
    return endsWithTypeId(typeId, 'categorySubmenuSlot');
}

function parseCategorySubmenuSlotComponent(component: ShopperExperience.schemas['Component']): ComponentType | null {
    const data = getDataRecord(component.data);
    const categoryId = typeof data.categoryId === 'string' ? data.categoryId.trim() : '';

    if (!categoryId) {
        return null;
    }

    return component as ComponentType;
}

type RegionContainer = Pick<ShopperExperience.schemas['Component'], 'regions'> | null | undefined;

/**
 * Reads stacked category submenu slot components from the burger menu embedded host and maps
 * each slot to its configured category ID.
 */
export function parseCategorySubmenuSlots(container: RegionContainer): CategorySubmenuSlots {
    const slots: CategorySubmenuSlots = {};
    const region = container?.regions?.find((entry) => entry.id === GLOBAL_CATEGORY_SUBMENU_SLOTS_REGION_ID);

    if (!region?.components?.length) {
        return slots;
    }

    for (const component of region.components) {
        if (!isCategorySubmenuSlotComponent(component.typeId)) {
            continue;
        }

        const parsed = parseCategorySubmenuSlotComponent(component);
        if (!parsed) {
            continue;
        }

        const categoryId = getDataRecord(parsed.data).categoryId;
        if (typeof categoryId === 'string' && categoryId.trim()) {
            slots[categoryId.trim()] = parsed;
        }
    }

    return slots;
}

export function getCategorySubmenuSlot(categoryId: string, slots: CategorySubmenuSlots): ComponentType | undefined {
    return slots[categoryId];
}
